import { BaseExportService } from './BaseExportService';

/**
 * South African Criminal Check Export Service
 *
 * Handles export of criminal check data from the service.
 * Supports the order-response shape (search_results, order_profiles, payment_instructions)
 * and the results-polling shape (HuruCriminalCheckResultsResponse with a `results` array).
 */
export class HuruCriminalCheckExportService extends BaseExportService {
  constructor(data, metadata = {}) {
    super(data, metadata);

    this.order_data = this.extract_order_data();
    this.results_data = this.extract_results_data();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Extraction helpers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Return the root response object regardless of nesting.
   * Handles both `data` directly and `data.result` wrapping.
   */
  get_root() {
    return this.data?.result || this.data || {};
  }

  /**
   * Extract the order-level response fields.
   */
  extract_order_data() {
    const root = this.get_root();
    return {
      status: root.status || null,
      message: root.message || null,
      order_number: root.order_number || null,
      order_date: root.order_date || null,
      order_status: root.order_status || null,
      order_method: root.order_method || null,
      transaction_id: root.transaction_id ?? null,
      search_message: root.search_message || null,
      huru_unique_ref: root.huru_unique_ref || null,
      search_results: root.search_results || [],
      order_profiles: root.order_profiles || [],
      payment_instructions: root.payment_instructions || null,
      meta_data: root.meta_data || null,
    };
  }

  /**
   * Extract HuruCriminalCheckResultsResponse-shape data (results polling endpoint).
   * Falls back gracefully when `results` is not present.
   */
  extract_results_data() {
    const root = this.get_root();
    return root.results || [];
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // BaseExportService overrides
  // ─────────────────────────────────────────────────────────────────────────────

  get_filename_prefix() {
    const ref =
      this.metadata?.order_number ||
      this.order_data.order_number ||
      this.order_data.huru_unique_ref ||
      'Unknown';
    return `Huru_CriminalCheck_${ref}`;
  }

  get_available_sections() {
    const sections = {
      subject_info: 'Subject Information',
      order_summary: 'Order Summary',
      search_results: 'Search Results',
      order_profiles: 'Order Profiles',
      result_items: 'Result Items (Polling)',
      payment_details: 'Payment Details',
      meta_data: 'Metadata',
      raw_json: 'Raw JSON Data',
    };

    // Hide sections that have no data
    if (!this.order_data.search_results?.length) delete sections.search_results;
    if (!this.order_data.order_profiles?.length) delete sections.order_profiles;
    if (!this.results_data?.length) delete sections.result_items;
    if (!this.order_data.payment_instructions) delete sections.payment_details;

    return sections;
  }

  get_default_export_options() {
    const defaults = {
      subject_info: true,
      order_summary: true,
      search_results: false,
      order_profiles: false,
      result_items: false,
      payment_details: false,
      meta_data: false,
      raw_json: false,
    };

    // Auto-enable sections that have data
    if (this.order_data.search_results?.length) defaults.search_results = true;
    if (this.order_data.order_profiles?.length) defaults.order_profiles = true;
    if (this.results_data?.length) defaults.result_items = true;

    return defaults;
  }

  prepare_export_data(export_options) {
    const export_data = {};

    if (export_options.subject_info) {
      export_data.subject_info = this.prepare_subject_info();
    }
    if (export_options.order_summary) {
      export_data.order_summary = this.prepare_order_summary();
    }
    if (export_options.search_results) {
      export_data.search_results = this.prepare_search_results();
    }
    if (export_options.order_profiles) {
      export_data.order_profiles = this.prepare_order_profiles();
    }
    if (export_options.result_items) {
      export_data.result_items = this.prepare_result_items();
    }
    if (export_options.payment_details) {
      export_data.payment_details = this.prepare_payment_details();
    }
    if (export_options.meta_data) {
      export_data.meta_data = this.prepare_meta_data();
    }
    if (export_options.raw_json) {
      export_data.raw_json = [this.prepare_raw_json_data()];
    }

    return export_data;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Section preparers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Subject information sourced from the top-level HuruCriminalCheck record
   * fields passed via `metadata` (identifier, names, status, timestamps, etc.).
   */
  prepare_subject_info() {
    const m = this.metadata || {};
    return [
      {
        'Identifier (ID/Passport)': this.safe_val(m.identifier),
        'ID Type': this.safe_val(m.id_type),
        'First Name': this.safe_val(m.first_name),
        'Last Name': this.safe_val(m.last_name),
        'ID Verification Requested': m.id_verification != null ? String(m.id_verification) : 'N/A',
        'Reason': this.safe_val(m.reason),
        'Order Number': this.safe_val(m.order_number),
        'Record Status': this.safe_val(m.record_status),
        'Created At': m.created_at ? this.format_iso_date(m.created_at) : 'N/A',
        'Updated At': m.updated_at ? this.format_iso_date(m.updated_at) : 'N/A',
        'PDF Document Available': m.has_document ? 'Yes' : 'No',
      },
    ];
  }

  /**
   * Top-level order summary row.
   */
  prepare_order_summary() {
    const o = this.order_data;
    return [
      {
        'Status': this.safe_val(o.status),
        'Message': this.safe_val(o.message),
        'Order Number': this.safe_val(o.order_number),
        'Order Date': this.format_iso_date(o.order_date),
        'Order Status': this.safe_val(o.order_status),
        'Order Method': this.safe_val(o.order_method),
        'Transaction ID': o.transaction_id ?? 'N/A',
        'Search Message': this.safe_val(o.search_message),
        'Huru Unique Ref': this.safe_val(o.huru_unique_ref),
        // PDF: prefer the S3 document flag from metadata, fall back to embedded PdfReport
        'PDF Available': (this.metadata?.has_document || !!this.get_root()?.PdfReport) ? 'Yes' : 'No',
      },
    ];
  }

  /**
   * Search results – one row per matched profile.
   */
  prepare_search_results() {
    const results = this.order_data.search_results;
    if (!results?.length) return [{ Info: 'No search results found.' }];

    return results.map((r, i) => ({
      '#': i + 1,
      'Initials': this.safe_val(r.intials),
      'Surname': this.safe_val(r.surname),
      'Masked Identity': this.safe_val(r.maskedidentity),
      'Profile Ref': this.safe_val(r.profileref),
      'Score': r.score ?? 'N/A',
      'Consent': this.safe_val(r.consent),
      'Masked DOB': this.safe_val(r.masked_dob),
      'Created': this.safe_val(r.created),
      'Last Used': this.safe_val(r.lastused),
    }));
  }

  /**
   * Order profiles – one row per profile, with references flattened.
   */
  prepare_order_profiles() {
    const profiles = this.order_data.order_profiles;
    if (!profiles?.length) return [{ Info: 'No order profiles found.' }];

    return profiles.map((p, i) => {
      const refs_flat = Array.isArray(p.your_references)
        ? p.your_references.map((ref) => `${ref.refdescription}: ${ref.refvalue}`).join(' | ')
        : 'N/A';

      return {
        '#': i + 1,
        'Profile Ref': this.safe_val(p.profileref),
        'Report ID': p.reportid ?? 'N/A',
        'Product': this.safe_val(p.product),
        'Masked Identifier': this.safe_val(p.maskedidentifier),
        'Masked DOB': this.safe_val(p.masked_dob),
        'Initials': this.safe_val(p.initials),
        'Surname': this.safe_val(p.surname),
        'Total': p.total ?? 'N/A',
        'Your References': refs_flat,
      };
    });
  }

  /**
   * Result items from the HuruCriminalCheckResultsResponse polling shape.
   */
  prepare_result_items() {
    const items = this.results_data;
    if (!items?.length) return [{ Info: 'No result items found.' }];

    return items.map((item, i) => {
      const refs_flat = Array.isArray(item.your_references)
        ? item.your_references.map((ref) => `${ref.refdescription}: ${ref.refvalue}`).join(' | ')
        : 'N/A';

      return {
        '#': i + 1,
        'Report ID': item.reportid ?? 'N/A',
        'Order Number': this.safe_val(item.ordernumber),
        'Outcome': this.safe_val(item.outcome),
        'Adverse': this.safe_val(item.adverse),
        'Profile Ref': this.safe_val(item.profileref),
        'Product': this.safe_val(item.product),
        'Initials': this.safe_val(item.intials),
        'Surname': this.safe_val(item.surname),
        'Status': this.safe_val(item.status),
        'ID Verified': this.safe_val(item.idverified),
        'Matching Surname': this.safe_val(item.matchingsurname),
        'Masked Identity': this.safe_val(item.maskedidentity),
        'Masked DOB': this.safe_val(item.masked_dob),
        'Requested': this.safe_val(item.requested),
        'Completed': this.safe_val(item.completed),
        'Your References': refs_flat,
        'Report File': this.safe_val(item.report),
      };
    });
  }

  /**
   * Payment instruction details.
   */
  prepare_payment_details() {
    const pi = this.order_data.payment_instructions;
    if (!pi) return [{ Info: 'No payment details found.' }];

    return [
      {
        'Payment URL': this.safe_val(pi.paymenturl),
        'Total (ex. VAT)': pi.total_exvat ?? 'N/A',
        'Total (inc. VAT)': pi.total_inc ?? 'N/A',
        'Description': this.safe_val(pi.description),
        'Quantity': pi.qty ?? 'N/A',
        'Expires': this.format_iso_date(pi.expires),
        'Payment Ref': this.safe_val(pi.paymentref),
      },
    ];
  }

  /**
   * Response metadata.
   */
  prepare_meta_data() {
    const m = this.order_data.meta_data;
    if (!m) return [{ Info: 'No metadata found.' }];

    return [
      {
        'Response Code': this.safe_val(m.response_code),
        'Client Reference': this.safe_val(m.client_reference),
        'Request Reference': this.safe_val(m.request_reference),
        'Duration (ms)': this.safe_val(m.duration),
        'Timestamp': this.safe_val(m.timestamp),
      },
    ];
  }

  /**
   * Full raw JSON representation.
   */
  prepare_raw_json_data() {
    return this.get_root();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Utility helpers
  // ─────────────────────────────────────────────────────────────────────────────

  safe_val(val) {
    if (val === null || val === undefined || val === 'None' || val === '') return 'N/A';
    return val;
  }

  /**
   * Pretty-format an ISO 8601 date string.
   */
  format_iso_date(date_str) {
    if (!date_str) return 'N/A';
    try {
      return new Date(date_str).toLocaleString();
    } catch (_) {
      return date_str;
    }
  }
}
